import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, OnInit, Output, QueryList, TemplateRef, ViewChildren } from '@angular/core';
import { DateUtil } from 'src/app/shared/util/DateUtil';
import { CalendarClickEvent } from '../../models/calendar-click-event';
import { CalendarEventHandler } from '../../models/calendar-event-handler';
import { CalendarEvent } from '../../models/calendar-event';
import { DayIndexUtil } from '../../util/DayIndexUtil';
import { DayTemplateContext, ICalendarComponent } from '../../interface/calendar';
import { daysBetween } from '../../../dates/models/DateFunctions';
import { CalendarSettings } from '../../models/calendar-settings';
import { CalendarDate } from '../../models/calendar-date';

/** 
 * A month calendar.  
 * For documentation see: {@link ICalendarComponent} 
*/
@Component({
  selector: 'app-calendar-month[date]',
  templateUrl: './calendar-month.component.html',
  styleUrls: ['./calendar-month.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CalendarMonthComponent implements OnInit, OnDestroy, ICalendarComponent {

  protected DateUtil = DateUtil;

  @Input() set date(value: Date) {
    this._date = value;
    this.dates = this.getAllDaysInMonth(value).map(e => ({ date: e, selected: false }));

    this.refreshEvents();
  }
  public get date(): Date {
    return this._date;
  }

  @Input() public eventHandler: CalendarEventHandler;
  @Input() public dayTemplate: TemplateRef<DayTemplateContext>
  @Input() public settings: CalendarSettings;

  @Output() public eventClick: EventEmitter<CalendarClickEvent> = new EventEmitter();

  protected dates: CalendarDate[] = [];

  protected get firstWeek(): Date[] {
    return this.dates.slice(0, 7).map(e => e.date);
  }

  protected today: Date;
  protected yesterday: Date;

  /**
   * Calendar events mapped to each day of the month.
   */
  protected eventIndex: CalendarEvent[][][] = [];

  private _date: Date;
  private mouseDown: boolean = false;

  private selectStart: CalendarDate;
  private currentHover: CalendarDate;

  constructor(private ngZone: NgZone) {
  }

  ngOnInit(): void {
    this.today = new Date();

    this.yesterday = new Date();
    this.yesterday.setDate(this.today.getDate() - 1)

    this.refreshEvents();
  }

  public refreshEvents() {
    this.eventIndex = this.getEvents();
  }

  ngOnDestroy(): void {

  }

  protected trackByDate(date: Date): number {
    return date.getTime();
  }

  protected trackByIndex(index: number): number {
    return index;
  }

  @HostListener('mousedown', ['$event'])
  protected mouseDownEvent(event: MouseEvent) {
    this.mouseDown = true;

    this.selectStart = this.currentHover;

    if (this.currentHover)
      this.currentHover.selected = true;
  }

  @HostListener('mouseup', ['$event'])
  protected mouseUpEvent(event: MouseEvent) {
    this.mouseDown = false;
  }

  //#region Selection
  protected mouseEnterOnDate(event: MouseEvent, date: CalendarDate) {
    this.currentHover = date;

    if (this.mouseDown) {
      date.selected = true;

      //Check which dates have been selected, then connect them together.
      const selectedDateIndex: number = this.getIndexOfDate(this.selectStart.date);
      const mouseEnterDateIndex: number = this.getIndexOfDate(date.date);

      const forwards: boolean = date.date.getTime() - this.selectStart.date.getTime() > 0;

      const start = selectedDateIndex;
      const end = mouseEnterDateIndex;
      const step = forwards ? 1 : -1;

      for (let index = start; forwards ? index < end : index > end; index += step) {
        const element = this.dates[index];
        element.selected = true;
      }
    }
  }
  //#endregion

  private getEvents(): CalendarEvent[][][] {
    if (!this.eventHandler)
      return;

    const startDate = this.dates[0].date;
    const endDate = this.dates[this.dates.length - 1].date;

    let events = this?.eventHandler(startDate, endDate);

    events = [...events].sort((a, b) => DateUtil.sortChronological(a.startDate, a.endDate, b.startDate, b.endDate))

    let eventIndex = [];

    events.forEach(e => {
      if (e.startDate < startDate || e.endDate > endDate)
        return;

      let month = eventIndex[e.startDate.getMonth()];

      if (!month) {
        month = new Array(daysBetween(new Date(e.startDate.getFullYear(), e.startDate.getMonth(), 1), new Date(e.startDate.getFullYear(), e.startDate.getMonth() + 1, 0)))
        eventIndex[e.startDate.getMonth()] = month;
      }

      let day = month[e.startDate.getDate()]

      if (!day) {
        day = [];
        month[e.startDate.getDate()] = day;
      }

      day.push(e);
    })

    return eventIndex;
  }

  private getAllDaysInMonth(date: Date): Date[] {
    const dates: Date[] = []

    const year = date.getFullYear();
    const month = date.getMonth();

    //Get the first day in this month.
    const firstDayOfMonth = new Date(year, month, 1);

    //Get the day of the week, of the first day in the month.
    const diffToMonday = (firstDayOfMonth.getDay() + 6) % 7;

    //Get the first monday that appears on the calendar (can be from the month before!)
    const startDate = new Date(year, month, 1 - diffToMonday);

    const lastDayOfMonth = new Date(startDate);
    lastDayOfMonth.setDate(lastDayOfMonth.getDate() + 42);

    for (let i = startDate; i < lastDayOfMonth; i.setDate(i.getDate() + 1)) {
      dates.push(new Date(i));
    }

    //Return all dates
    return dates;
  }

  private getIndexOfDate(date: Date): number {
    return DateUtil.millisecondsToDays(date.getTime() - this.dates[0].date.getTime());
  }
}
